/**
 * Helpers de marquage tenant pour les exports PDF et les prints navigateur
 * (US-TR-06 + US-TR-08).
 *
 *  - `wrapWithTenantBrand` : clone un noeud DOM, l'enveloppe d'un header
 *    (logo + nom tenant + bande couleur primaire) et d'un footer "Genere le …".
 *    Le wrapper est attache offscreen pour que html2pdf puisse le rasteriser
 *    sans perturber l'affichage. L'appelant doit appeler le `cleanup` retourne
 *    apres l'export pour le supprimer.
 *  - `tenantBrandPrintStyle` : feuille de style additionnelle a injecter dans
 *    une `.print-zone` pour qu'elle reprenne la couleur primaire en bordure
 *    + logo + nom tenant en en-tete a l'impression navigateur (window.print).
 *
 * Le branding tenant provient de `useTenantSettings()`. Pour les super-admins
 * plateforme (settings === null), `wrapWithTenantBrand` se rabat sur un
 * en-tete generique "mygmao.pro" en couleur primaire par defaut.
 */

export interface TenantBrandInfo {
  /** Nom du tenant (ex. "Clinique Al Madina"). Tombe sur "mygmao.pro" si absent. */
  name?: string | null;
  /** URL signee MinIO du logo, ou URL externe. Optionnel. */
  logoUrl?: string | null;
  /** Couleur primaire (#hex). Defaut #20947C (vert mygmao). */
  primaryColor?: string | null;
}

const DEFAULT_PRIMARY = '#20947C';
const APP_NAME = 'mygmao.pro';

/** Dimensions A4 en millimètres (largeur du bord court). */
const A4_PORTRAIT_WIDTH_MM = 210;
const A4_LANDSCAPE_WIDTH_MM = 297;
const PX_PER_MM = 96 / 25.4; // 96 dpi

/**
 * Largeur (px @96dpi) de la ZONE DE CONTENU d'une page A4 pour html2pdf,
 * c.-à-d. largeur de page MOINS les marges gauche+droite.
 *
 * CRITIQUE : le wrapper passé à html2pdf doit avoir EXACTEMENT cette largeur.
 * html2pdf rend l'élément dans cette zone (page − marges) ; si le wrapper est
 * plus large (ex. pleine page 210mm), la partie droite est rognée → contenu
 * coupé à droite (header, valeurs alignées à droite, footer).
 */
export function a4ContentWidthPx(
  orientation: 'portrait' | 'landscape' = 'portrait',
  marginMm = 10,
): number {
  const pageWidthMm = orientation === 'landscape' ? A4_LANDSCAPE_WIDTH_MM : A4_PORTRAIT_WIDTH_MM;
  return Math.floor((pageWidthMm - 2 * marginMm) * PX_PER_MM);
}

/** Echappe un texte avant injection dans innerHTML. */
function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Construit un wrapper offscreen pour l'export PDF : header (logo + nom tenant
 * + bande couleur primaire) + clone du noeud source + footer "Genere le …".
 *
 * Retourne le noeud cible a passer a `html2pdf().from(...)` ET une fonction
 * `cleanup()` a appeler une fois l'export termine (succes ou echec) pour
 * retirer le wrapper du DOM.
 */
export function wrapWithTenantBrand(
  source: HTMLElement,
  brand: TenantBrandInfo,
  opts: { widthPx?: number } = {},
): { target: HTMLElement; cleanup(): void } {
  // Largeur de la zone de contenu PDF (page − marges). Doit correspondre à la
  // largeur effective de rendu de html2pdf, sinon le contenu est rogné à droite.
  const widthPx = opts.widthPx ?? a4ContentWidthPx('portrait', 10);
  const widthCss = `${widthPx}px`;
  const primary = brand.primaryColor || DEFAULT_PRIMARY;
  const name = (brand.name ?? '').trim() || APP_NAME;
  const generatedAt = new Date().toLocaleString('fr-FR', {
    dateStyle: 'short',
    timeStyle: 'short',
  });

  // IMPORTANT : l'élément passé à html2pdf DOIT rester dans le flux normal
  // (position statique). html2pdf.js clone l'élément cible dans son propre
  // conteneur de rendu ; si la cible est `position: fixed`/`absolute`, le clone
  // sort du flux → le conteneur a une hauteur nulle → html2canvas produit un
  // canvas de hauteur 0 → PDF page blanche.
  //
  // On isole donc le positionnement offscreen sur un PARENT (`host`) et on rend
  // le `wrapper` (la cible html2pdf) en flux normal à l'intérieur.
  const host = document.createElement('div');
  host.style.cssText = [
    'position: fixed',
    'left: -10000px',
    'top: 0',
    `width: ${widthCss}`,
    'pointer-events: none',
  ].join(';');

  const wrapper = document.createElement('div');
  wrapper.style.cssText = [
    `width: ${widthCss}`, // = zone de contenu PDF (page − marges)
    'background: white',
    'font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
    'color: #111827',
    'box-sizing: border-box',
  ].join(';');

  // ── Header : bande couleur primaire + logo + nom tenant ──────────────────
  const header = document.createElement('div');
  header.style.cssText = [
    'display: flex',
    'align-items: center',
    'justify-content: space-between',
    'padding: 16px 20px',
    `border-top: 6px solid ${primary}`,
    'border-bottom: 1px solid #e5e7eb',
    'gap: 16px',
  ].join(';');

  const headerLeft = document.createElement('div');
  headerLeft.style.cssText = 'display: flex; align-items: center; gap: 12px;';
  if (brand.logoUrl) {
    const logo = document.createElement('img');
    logo.src = brand.logoUrl;
    logo.alt = '';
    logo.crossOrigin = 'anonymous';
    logo.style.cssText = 'height: 40px; width: auto; max-width: 160px; object-fit: contain;';
    headerLeft.appendChild(logo);
  }
  const headerName = document.createElement('div');
  headerName.innerHTML = `<div style="font-size: 16px; font-weight: 600; color: #111827;">${escapeHtml(name)}</div><div style="font-size: 11px; color: #6b7280; margin-top: 2px;">${escapeHtml(APP_NAME)}</div>`;
  headerLeft.appendChild(headerName);

  const headerRight = document.createElement('div');
  headerRight.style.cssText = 'font-size: 11px; color: #6b7280; text-align: right;';
  headerRight.textContent = `Genere le ${generatedAt}`;

  header.appendChild(headerLeft);
  header.appendChild(headerRight);

  // ── Body : clone du noeud source ─────────────────────────────────────────
  const body = document.createElement('div');
  body.style.cssText = 'padding: 16px 20px;';
  const clone = source.cloneNode(true) as HTMLElement;
  body.appendChild(clone);

  // ── Footer ───────────────────────────────────────────────────────────────
  const footer = document.createElement('div');
  footer.style.cssText = [
    'padding: 10px 20px',
    `border-top: 2px solid ${primary}`,
    'display: flex',
    'justify-content: space-between',
    'font-size: 10px',
    'color: #6b7280',
  ].join(';');
  footer.innerHTML = `<span>${escapeHtml(name)}</span><span>${escapeHtml(APP_NAME)}</span>`;

  wrapper.appendChild(header);
  wrapper.appendChild(body);
  wrapper.appendChild(footer);
  host.appendChild(wrapper);
  document.body.appendChild(host);

  return {
    // La cible passée à html2pdf est le `wrapper` IN-FLOW (pas le `host`
    // positionné), pour que html2pdf mesure correctement sa hauteur.
    target: wrapper,
    cleanup: () => {
      host.remove();
    },
  };
}

/**
 * Feuille de style additionnelle pour injecter le branding tenant dans une
 * `.print-zone`. A inserer dans un `<style>` cote composant qui imprime
 * via `window.print()` (cas QR labels).
 *
 * Utilise les CSS variables `--brand-primary` / `--brand-name` / `--brand-logo`
 * qu'on definit en inline-style sur la print-zone elle-meme.
 */
export function buildTenantPrintCss(): string {
  return `
    @media print {
      body * { visibility: hidden !important; }
      .print-zone, .print-zone * { visibility: visible !important; }
      .print-zone {
        position: fixed !important;
        left: 0 !important;
        top: 0 !important;
        right: 0 !important;
        margin: 0 !important;
        padding: 16px !important;
        border: none !important;
        border-top: 6px solid var(--brand-primary, ${DEFAULT_PRIMARY}) !important;
        border-bottom: 2px solid var(--brand-primary, ${DEFAULT_PRIMARY}) !important;
        width: 100% !important;
      }
      .no-print { display: none !important; }
    }
  `;
}

export { DEFAULT_PRIMARY as DEFAULT_BRAND_PRIMARY, APP_NAME as BRAND_APP_NAME };
